import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Package, Sprout, MessageCircle, Star, TrendingUp, AlertCircle, CheckCircle, Clock, XCircle, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { api } from '@/lib/api-client';
import type { SellerProfile, UserSubscription, SubscriptionPlan, PackageListing, HarvestListing, Inquiry } from '@shared/types';

// Mock userId - in real app, get from auth context
const MOCK_USER_ID = 'user-123';

export function SellerDashboardPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState<{ id: string; type: 'package' | 'harvest' } | null>(null);
  const [editListing, setEditListing] = useState<PackageListing | HarvestListing | null>(null);

  // Fetch seller profile
  const { data: sellerProfile, isLoading: loadingProfile } = useQuery({
    queryKey: ['seller-profile', MOCK_USER_ID],
    queryFn: () => api<SellerProfile>(`/api/sellers?userId=${MOCK_USER_ID}`).then(profiles => profiles[0]),
  });

  // Fetch subscription
  const { data: subscription, isLoading: loadingSubscription } = useQuery({
    queryKey: ['user-subscription', MOCK_USER_ID],
    queryFn: () => api<UserSubscription>(`/api/users/${MOCK_USER_ID}/subscription`),
    enabled: !!sellerProfile,
  });

  // Fetch subscription plan details
  const { data: plans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api<SubscriptionPlan[]>('/api/subscription-plans'),
    enabled: !!subscription,
  });

  const currentPlan = plans?.find(p => p.id === subscription?.planId);

  // Fetch seller's listings
  const { data: packageListings } = useQuery({
    queryKey: ['seller-packages', sellerProfile?.id],
    queryFn: () => api<PackageListing[]>(`/api/sellers/${sellerProfile!.id}/listings?type=package`),
    enabled: !!sellerProfile,
  });

  const { data: harvestListings } = useQuery({
    queryKey: ['seller-harvests', sellerProfile?.id],
    queryFn: () => api<HarvestListing[]>(`/api/sellers/${sellerProfile!.id}/listings?type=harvest`),
    enabled: !!sellerProfile,
  });

  // Fetch inquiries
  const { data: inquiries } = useQuery({
    queryKey: ['inquiries-received', MOCK_USER_ID],
    queryFn: () => api<Inquiry[]>(`/api/inquiries/received?userId=${MOCK_USER_ID}`),
    enabled: !!sellerProfile,
  });

  // Delete listing mutation
  const deleteListingMutation = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: 'package' | 'harvest' }) => {
      return api(`/api/listings/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-packages'] });
      queryClient.invalidateQueries({ queryKey: ['seller-harvests'] });
      toast.success('Listing deleted');
      setDeleteDialogOpen(false);
      setListingToDelete(null);
    },
    onError: () => {
      toast.error('Failed to delete listing');
    },
  });

  // Update listing status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return api(`/api/listings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-packages'] });
      queryClient.invalidateQueries({ queryKey: ['seller-harvests'] });
      toast.success('Status updated');
    },
  });

  const isLoading = loadingProfile || loadingSubscription;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // No seller profile - show onboarding
  if (!sellerProfile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Card>
            <CardHeader>
              <CardTitle>Become a Seller</CardTitle>
              <CardDescription>
                Create your seller profile to start listing packages and harvests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Get Started</AlertTitle>
                <AlertDescription>
                  You need to create a seller profile before you can list products. 
                  This helps buyers trust your listings and allows you to build your reputation.
                </AlertDescription>
              </Alert>
              <Button className="w-full">Create Seller Profile</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const verificationStatusIcon = {
    unverified: <AlertCircle className="h-4 w-4 text-muted-foreground" />,
    pending: <Clock className="h-4 w-4 text-yellow-600" />,
    verified: <CheckCircle className="h-4 w-4 text-green-600" />,
    rejected: <XCircle className="h-4 w-4 text-red-600" />,
  }[sellerProfile.verificationStatus];

  const totalListings = (packageListings?.length || 0) + (harvestListings?.length || 0);
  const pendingInquiries = inquiries?.filter(i => i.status === 'pending').length || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{sellerProfile.businessName}</h1>
              <div className="flex items-center gap-2">
                {verificationStatusIcon}
                <span className="text-sm text-muted-foreground capitalize">
                  {sellerProfile.verificationStatus.replace('_', ' ')}
                </span>
                {sellerProfile.rating > 0 && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">{sellerProfile.rating.toFixed(1)}</span>
                      <span className="text-sm text-muted-foreground">({sellerProfile.totalReviews})</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <Button>Edit Profile</Button>
          </div>
        </div>

        {/* Subscription Alert */}
        {subscription?.status !== 'active' && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Subscription Required</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>You need an active subscription to create listings.</span>
              <Button size="sm" variant="outline" asChild>
                <a href="/pricing">View Plans</a>
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <StatsCard
            icon={<Package className="h-4 w-4" />}
            label="Active Listings"
            value={sellerProfile.activeListings}
            description={`${totalListings} total`}
          />
          <StatsCard
            icon={<MessageCircle className="h-4 w-4" />}
            label="Pending Inquiries"
            value={pendingInquiries}
            description={`${inquiries?.length || 0} total`}
          />
          <StatsCard
            icon={<TrendingUp className="h-4 w-4" />}
            label="Total Views"
            value={0}
            description="Across all listings"
          />
          <StatsCard
            icon={<Star className="h-4 w-4" />}
            label="Rating"
            value={sellerProfile.rating.toFixed(1)}
            description={`${sellerProfile.totalReviews} reviews`}
          />
        </div>

        {/* Subscription Card */}
        {subscription && currentPlan && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Current Plan: {currentPlan.name}</CardTitle>
                  <CardDescription>
                    {subscription.status === 'active' && subscription.endDate && (
                      <>Renews on {new Date(subscription.endDate).toLocaleDateString()}</>
                    )}
                  </CardDescription>
                </div>
                <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                  {subscription.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Listing Limit</p>
                  <p className="font-medium">
                    {currentPlan.features.listingLimit === -1 ? 'Unlimited' : currentPlan.features.listingLimit}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Featured Listings</p>
                  <p className="font-medium">{currentPlan.features.featuredListings}/month</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Priority Placement</p>
                  <p className="font-medium">{currentPlan.features.priorityPlacement ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Verified Badge</p>
                  <p className="font-medium">{currentPlan.features.verifiedBadge ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="packages">
              Package Listings ({packageListings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="harvests">
              Harvest Listings ({harvestListings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="inquiries">
              Inquiries ({inquiries?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  New Package Listing
                </Button>
                <Button variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  New Harvest Listing
                </Button>
                <Button variant="outline">View Analytics</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="packages" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Package Listings</h3>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Package
              </Button>
            </div>
            {packageListings && packageListings.length > 0 ? (
              <div className="grid gap-4">
                {packageListings.map(listing => (
                  <ListingRow
                    key={listing.id}
                    listingId={listing.id}
                    listingType="package"
                    title={listing.title}
                    status={listing.status}
                    views={listing.views}
                    inquiries={listing.inquiries}
                    price={`${listing.currency === 'GHS' ? '₵' : listing.currency}${listing.price}`}
                    onDelete={(id, type) => { setListingToDelete({ id, type }); setDeleteDialogOpen(true); }}
                    onUpdateStatus={(id, status) => updateStatusMutation.mutate({ id, status })}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No package listings yet. Create your first one to get started.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="harvests" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Harvest Listings</h3>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Harvest
              </Button>
            </div>
            {harvestListings && harvestListings.length > 0 ? (
              <div className="grid gap-4">
                {harvestListings.map(listing => (
                  <ListingRow
                    key={listing.id}
                    listingId={listing.id}
                    listingType="harvest"
                    title={listing.productName}
                    status={listing.status}
                    views={listing.views}
                    inquiries={listing.inquiries}
                    price={`${listing.currency === 'GHS' ? '₵' : listing.currency}${listing.pricePerUnit}/${listing.unit}`}
                    onDelete={(id, type) => { setListingToDelete({ id, type }); setDeleteDialogOpen(true); }}
                    onUpdateStatus={(id, status) => updateStatusMutation.mutate({ id, status })}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No harvest listings yet. Create your first one to get started.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="inquiries" className="space-y-4">
            <h3 className="text-lg font-semibold">Recent Inquiries</h3>
            {inquiries && inquiries.length > 0 ? (
              <div className="grid gap-4">
                {inquiries.map(inquiry => (
                  <InquiryRow key={inquiry.id} inquiry={inquiry} />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No inquiries yet.
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Listing</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this listing? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (listingToDelete) {
                    deleteListingMutation.mutate(listingToDelete);
                  }
                }}
                disabled={deleteListingMutation.isPending}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

interface StatsCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  description: string;
}

function StatsCard({ icon, label, value, description }: StatsCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          {icon}
          <span className="text-sm">{label}</span>
        </div>
        <div className="text-2xl font-bold mb-1">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

interface ListingRowProps {
  listingId?: string;
  listingType?: 'package' | 'harvest';
  title: string;
  status: string;
  views: number;
  inquiries: number;
  price: string;
  onDelete?: (id: string, type: 'package' | 'harvest') => void;
  onUpdateStatus?: (id: string, status: string) => void;
}

function ListingRow({ title, status, views, inquiries, price, listingId, listingType, onDelete, onUpdateStatus }: ListingRowProps) {
  const statusVariant = {
    active: 'default',
    draft: 'outline',
    pending_review: 'secondary',
    sold: 'secondary',
    expired: 'secondary',
    flagged: 'destructive',
    removed: 'destructive',
    available: 'default',
    sold_out: 'secondary',
  }[status] || 'outline';

  return (
    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <div className="flex-1">
          <h4 className="font-medium mb-1">{title}</h4>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Badge variant={statusVariant as any}>{status.replace('_', ' ')}</Badge>
            <span>{views} views</span>
            <span>{inquiries} inquiries</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold">{price}</span>
          <Select onValueChange={(newStatus) => onUpdateStatus?.(listingId, newStatus)} defaultValue={status}>
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="removed">Removed</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" asChild>
            <a href={`/marketplace/packages/${listingId}`}>View</a>
          </Button>
          <Button size="sm" variant="destructive" onClick={() => onDelete?.(listingId, listingType)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface InquiryRowProps {
  inquiry: Inquiry;
}

function InquiryRow({ inquiry }: InquiryRowProps) {
  const statusVariant = {
    pending: 'default',
    contacted: 'secondary',
    closed: 'outline',
  }[inquiry.status];

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-medium">{inquiry.buyerName}</p>
            <p className="text-sm text-muted-foreground">{inquiry.buyerPhone}</p>
          </div>
          <Badge variant={statusVariant as any}>{inquiry.status}</Badge>
        </div>
        <p className="text-sm mb-3">{inquiry.message}</p>
        <div className="flex gap-2">
          <Button size="sm">Respond</Button>
          {inquiry.status !== 'closed' && (
            <Button size="sm" variant="outline">Mark as Closed</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
